import { useCallback, useEffect, useRef, useState } from 'react';

import { chonKieuGhiAm, lyDoKhongGhiDuoc, phanLoaiLoiMicro, tenTepGhiAm, type LyDoMicro } from '../nghiep-vu/bien-ban';

export type TrangThaiGhiAm = 'cho' | 'xin-quyen' | 'dang-ghi' | 'da-ghi';

const moiTruong = () => ({
  baoMat: window.isSecureContext,
  coMediaDevices: Boolean(navigator.mediaDevices?.getUserMedia),
  coMediaRecorder: typeof MediaRecorder !== 'undefined',
});

/**
 * Ghi âm trên trình duyệt: xin micro, đếm giờ, dừng, cho nghe lại, trả về một `File` để gửi như tệp thường.
 * Không làm thời gian thực (ADR-0126). Rời hộp giữa chừng thì tắt micro và bỏ bản ghi.
 */
export function useGhiAm() {
  const [trangThai, setTrangThai] = useState<TrangThaiGhiAm>('cho');
  const [lyDoLoi, setLyDoLoi] = useState<LyDoMicro | null>(() => lyDoKhongGhiDuoc(moiTruong()));
  const [giay, setGiay] = useState(0);
  const [tep, setTep] = useState<File | null>(null);
  const [urlNghe, setUrlNghe] = useState<string | null>(null);
  const luong = useRef<MediaStream | null>(null);
  const may = useRef<MediaRecorder | null>(null);
  const hen = useRef<number | undefined>(undefined);
  const url = useRef<string | null>(null);
  const daRoi = useRef(false);

  const tatMicro = useCallback(() => {
    window.clearInterval(hen.current);
    luong.current?.getTracks().forEach((tr) => tr.stop());
    luong.current = null;
  }, []);

  const boBanGhi = useCallback(() => {
    if (url.current) URL.revokeObjectURL(url.current);
    url.current = null;
    setUrlNghe(null);
    setTep(null);
    setGiay(0);
  }, []);

  const batDau = useCallback(async () => {
    const khongDuoc = lyDoKhongGhiDuoc(moiTruong());
    if (khongDuoc) {
      setLyDoLoi(khongDuoc);
      return;
    }
    boBanGhi();
    setLyDoLoi(null);
    setTrangThai('xin-quyen');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setLyDoLoi(phanLoaiLoiMicro(e, window.isSecureContext));
      setTrangThai('cho');
      return;
    }
    if (daRoi.current) {
      stream.getTracks().forEach((tr) => tr.stop());
      return;
    }
    const kieu = chonKieuGhiAm((k) => MediaRecorder.isTypeSupported(k));
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, kieu ? { mimeType: kieu } : undefined);
    } catch (e) {
      stream.getTracks().forEach((tr) => tr.stop());
      setLyDoLoi(phanLoaiLoiMicro(e, true));
      setTrangThai('cho');
      return;
    }
    const manh: Blob[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) manh.push(e.data);
    };
    recorder.onstop = () => {
      tatMicro();
      if (daRoi.current) return;
      const loai = recorder.mimeType || kieu || 'audio/webm';
      const blob = new Blob(manh, { type: loai });
      if (blob.size === 0) {
        setLyDoLoi('KHONG_CO_TIENG');
        setTrangThai('cho');
        return;
      }
      url.current = URL.createObjectURL(blob);
      setUrlNghe(url.current);
      setTep(new File([blob], tenTepGhiAm(loai, new Date()), { type: loai.split(';')[0] ?? loai }));
      setTrangThai('da-ghi');
    };
    luong.current = stream;
    may.current = recorder;
    // Nhận mảnh mỗi giây: máy treo giữa lúc ghi cũng không mất cả bản.
    recorder.start(1000);
    const luc = Date.now();
    setGiay(0);
    hen.current = window.setInterval(() => setGiay(Math.floor((Date.now() - luc) / 1000)), 500);
    setTrangThai('dang-ghi');
  }, [boBanGhi, tatMicro]);

  const dung = useCallback(() => {
    if (may.current?.state === 'recording') may.current.stop();
  }, []);

  const lamLai = useCallback(() => {
    boBanGhi();
    setLyDoLoi(null);
    setTrangThai('cho');
  }, [boBanGhi]);

  useEffect(() => {
    // StrictMode chạy dọn dẹp rồi dựng lại ngay: phải đặt lại cờ, nếu không lượt dựng thật coi như đã rời.
    daRoi.current = false;
    return () => {
      daRoi.current = true;
      if (may.current?.state === 'recording') may.current.stop();
      tatMicro();
      if (url.current) URL.revokeObjectURL(url.current);
    };
  }, [tatMicro]);

  return { trangThai, lyDoLoi, giay, tep, urlNghe, batDau, dung, lamLai };
}
